import { useState, useCallback, useEffect } from 'react';

export interface RateLimitStatus {
  limit: number | 'unlimited';
  remaining: number | 'unlimited';
  resetInSeconds: number;
  exempt: boolean;
  lastUpdated: Date | null;
}

const defaultStatus: RateLimitStatus = {
  limit: 20,
  remaining: 20,
  resetInSeconds: 60,
  exempt: false,
  lastUpdated: null,
};

// Global state to share across components
let globalStatus: RateLimitStatus = { ...defaultStatus };
let listeners: Set<() => void> = new Set();

function notifyListeners() {
  listeners.forEach(listener => listener());
}

export function useRateLimitStatus() {
  const [status, setStatus] = useState<RateLimitStatus>(globalStatus);

  // Subscribe to global updates
  useEffect(() => {
    const listener = () => setStatus({ ...globalStatus });
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const updateFromHeaders = useCallback((headers: Headers) => {
    const limitHeader = headers.get('X-RateLimit-Limit');
    const remainingHeader = headers.get('X-RateLimit-Remaining');
    const resetHeader = headers.get('X-RateLimit-Reset');
    const exemptHeader = headers.get('X-RateLimit-Exempt');

    if (limitHeader || remainingHeader || resetHeader || exemptHeader) {
      const exempt = exemptHeader === 'true';
      
      globalStatus = {
        limit: exempt || limitHeader === 'unlimited' ? 'unlimited' : parseInt(limitHeader || '20', 10),
        remaining: exempt || remainingHeader === 'unlimited' ? 'unlimited' : parseInt(remainingHeader || '20', 10),
        resetInSeconds: parseInt(resetHeader || '60', 10),
        exempt,
        lastUpdated: new Date(),
      };
      
      notifyListeners();
      
      // Dispatch event to trigger credit stats refresh
      window.dispatchEvent(new CustomEvent('ai-request-completed'));
    }
  }, []);

  const updateFromResponse = useCallback((response: Response) => {
    updateFromHeaders(response.headers);
  }, [updateFromHeaders]);

  const reset = useCallback(() => {
    globalStatus = { ...defaultStatus };
    notifyListeners();
  }, []);

  return {
    status,
    updateFromHeaders,
    updateFromResponse,
    reset,
  };
}

// Utility to extract rate limit info from a Supabase function response
export function extractRateLimitFromError(error: any): Partial<RateLimitStatus> | null {
  if (error?.status === 429) {
    return {
      remaining: 0,
      resetInSeconds: error?.data?.retryAfter || 60,
      exempt: false,
    };
  }
  return null;
}

// Dev tools: Expose test functions to manually set credits for UI testing
// These are always registered but only accessible when dev mode is enabled
const registerDevFunctions = () => {
  (window as any).__setTestCredits = (remaining: number, limit: number = 20) => {
    globalStatus = {
      limit,
      remaining,
      resetInSeconds: 60,
      exempt: false,
      lastUpdated: new Date(),
    };
    notifyListeners();
    // Dispatch event for useCreditStats to listen to
    window.dispatchEvent(new CustomEvent('dev-credits-changed', {
      detail: { remaining, limit, exempt: false, isDevMode: true }
    }));
    console.log(`[DEV] Credits set to ${remaining}/${limit}`);
  };

  (window as any).__setUnlimited = () => {
    globalStatus = {
      limit: 'unlimited',
      remaining: 'unlimited',
      resetInSeconds: 60,
      exempt: true,
      lastUpdated: new Date(),
    };
    notifyListeners();
    // Dispatch event for useCreditStats to listen to
    window.dispatchEvent(new CustomEvent('dev-credits-changed', {
      detail: { remaining: 'unlimited', limit: 'unlimited', exempt: true, isDevMode: true }
    }));
    console.log('[DEV] Account set to unlimited/exempt');
  };

  (window as any).__resetCredits = () => {
    globalStatus = { ...defaultStatus };
    notifyListeners();
    // Dispatch reset event
    window.dispatchEvent(new CustomEvent('dev-credits-reset'));
    console.log('[DEV] Credits reset to defaults');
  };
};

// Always register dev functions (they're only exposed in UI when dev mode is enabled)
registerDevFunctions();

// Re-register when dev mode changes (in case functions were cleared)
if (typeof window !== 'undefined') {
  window.addEventListener('dev-mode-changed', () => {
    registerDevFunctions();
  });
}
